import {
  AlertCircle,
  Mic,
  Pause,
  Play,
  Square,
  Trash2,
  Upload,
  Volume2,
} from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { useAudioRecorder } from "@/hooks/use-audio-recorder"
import { cn } from "@/lib/utils"

interface AudioUploaderProps {
  value?: File | null
  existingUrl?: string
  onChange: (file: File | null) => void
  onRemoveExisting?: () => void
  maxSizeMb?: number
  className?: string
}

const ACCEPTED_TYPES = [
  "audio/webm",
  "audio/mp4",
  "audio/ogg",
  "audio/mpeg",
  "audio/wav",
]

function formatDuration(seconds: number): string {
  // Handle invalid durations (NaN, Infinity, negative)
  if (!isFinite(seconds) || seconds < 0) {
    return "0:00"
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

export function AudioUploader({
  value,
  existingUrl,
  onChange,
  onRemoveExisting,
  maxSizeMb = 10,
  className,
}: AudioUploaderProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [playbackTime, setPlaybackTime] = useState(0)
  const [audioDuration, setAudioDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [objectUrl, setObjectUrl] = useState<string | null>(null)

  const audioRef = useRef<HTMLAudioElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number | null>(null)

  const recorder = useAudioRecorder()

  // Create and manage object URL for File value
  useEffect(() => {
    if (value) {
      // Create new object URL
      const url = URL.createObjectURL(value)
      setObjectUrl(url)

      // Cleanup on change or unmount
      return () => {
        URL.revokeObjectURL(url)
      }
    } else {
      setObjectUrl(null)
    }
  }, [value])

  // Get audio source - use object URL for File, existingUrl otherwise
  const audioSrc = objectUrl || existingUrl

  // Draw waveform while recording
  useEffect(() => {
    if (!recorder.isRecording) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    const draw = () => {
      const data = recorder.getFrequencyData()
      const width = canvas.width
      const height = canvas.height

      // Clear canvas
      ctx.fillStyle = "rgb(241, 245, 249)" // slate-100
      ctx.fillRect(0, 0, width, height)

      // Draw bars
      const barWidth = width / data.length
      const barGap = 2

      for (let i = 0; i < data.length; i++) {
        const barHeight = (data[i] / 255) * height * 0.8
        const x = i * barWidth
        const y = (height - barHeight) / 2

        ctx.fillStyle = "rgb(16, 185, 129)" // emerald-500
        ctx.fillRect(x + barGap / 2, y, barWidth - barGap, barHeight)
      }

      animationRef.current = requestAnimationFrame(draw)
    }

    draw()

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [recorder.isRecording, recorder.getFrequencyData])

  // Audio playback handlers
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => setPlaybackTime(audio.currentTime)
    const handleDurationChange = () => setAudioDuration(audio.duration || 0)
    const handleEnded = () => setIsPlaying(false)
    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    audio.addEventListener("timeupdate", handleTimeUpdate)
    audio.addEventListener("durationchange", handleDurationChange)
    audio.addEventListener("ended", handleEnded)
    audio.addEventListener("play", handlePlay)
    audio.addEventListener("pause", handlePause)

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate)
      audio.removeEventListener("durationchange", handleDurationChange)
      audio.removeEventListener("ended", handleEnded)
      audio.removeEventListener("play", handlePlay)
      audio.removeEventListener("pause", handlePause)
    }
  }, [value, existingUrl])

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(null)
      const file = e.target.files?.[0]
      if (!file) return

      // Validate type
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setError("Please upload an audio file (MP3, WAV, M4A, WebM, OGG)")
        return
      }

      // Validate size
      if (file.size > maxSizeMb * 1024 * 1024) {
        setError(`File too large. Maximum size is ${maxSizeMb}MB`)
        return
      }

      onChange(file)
    },
    [maxSizeMb, onChange]
  )

  const handleStartRecording = useCallback(async () => {
    setError(null)
    await recorder.start()
  }, [recorder])

  const handleStopRecording = useCallback(async () => {
    const blob = await recorder.stop()
    if (blob) {
      // Determine file extension based on MIME type
      const mimeType = blob.type || "audio/webm"
      let extension = "webm"
      if (mimeType.includes("mp4")) extension = "m4a"
      else if (mimeType.includes("ogg")) extension = "ogg"
      else if (mimeType.includes("mpeg")) extension = "mp3"

      console.log(
        `Recording stopped: ${blob.size} bytes, type: ${mimeType}, extension: ${extension}`
      )

      const file = new File([blob], `recording-${Date.now()}.${extension}`, {
        type: mimeType,
      })
      onChange(file)
    }
  }, [recorder, onChange])

  const handleRemove = useCallback(() => {
    onChange(null)
    setIsPlaying(false)
    setPlaybackTime(0)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }, [onChange])

  const togglePlayback = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) {
      audio.pause()
    } else {
      void audio.play()
    }
  }, [isPlaying])

  const hasAudio = value || existingUrl

  return (
    <div className={cn("space-y-3", className)}>
      {/* Hidden audio element */}
      {audioSrc && <audio ref={audioRef} src={audioSrc} preload="metadata" />}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Error display */}
      {(error || recorder.error) && (
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error ?? recorder.error}</span>
        </div>
      )}

      {/* Recording state */}
      {recorder.isRecording && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative flex items-center justify-center">
              <div className="absolute h-3 w-3 rounded-full bg-red-500 animate-pulse" />
              <div className="h-3 w-3 rounded-full bg-red-500" />
            </div>
            <span className="font-mono text-lg tabular-nums text-slate-700 dark:text-slate-300">
              {formatDuration(recorder.duration)}
            </span>
            <span className="text-sm text-slate-500">Recording...</span>
          </div>

          {/* Waveform visualization */}
          <canvas
            ref={canvasRef}
            width={320}
            height={48}
            className="w-full h-12 rounded-md bg-slate-100 dark:bg-slate-800"
          />

          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={() => void handleStopRecording()}
            className="gap-2"
          >
            <Square className="h-4 w-4" />
            Stop Recording
          </Button>
        </div>
      )}

      {/* Playback state */}
      {!recorder.isRecording && hasAudio && (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={togglePlayback}
            className="h-10 w-10 rounded-full bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/50 dark:hover:bg-emerald-900"
          >
            {isPlaying ? (
              <Pause className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
            ) : (
              <Play className="h-5 w-5 text-emerald-700 dark:text-emerald-400 ml-0.5" />
            )}
          </Button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-sm">
              <Volume2 className="h-4 w-4 text-slate-400" />
              <span className="font-medium text-slate-700 dark:text-slate-300 truncate">
                {value?.name ?? "Editor's Audio"}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span className="font-mono tabular-nums">
                {formatDuration(playbackTime)}
              </span>
              <span>/</span>
              <span className="font-mono tabular-nums">
                {formatDuration(audioDuration)}
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={existingUrl && !value ? onRemoveExisting : handleRemove}
            className="text-slate-400 hover:text-red-600 dark:hover:text-red-400"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Upload/Record buttons */}
      {!recorder.isRecording && !hasAudio && (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            Upload Audio
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleStartRecording()}
            className="gap-2"
          >
            <Mic className="h-4 w-4" />
            Record
          </Button>
        </div>
      )}

      {/* Replace buttons when audio exists */}
      {!recorder.isRecording && hasAudio && (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            className="gap-2 text-slate-500"
          >
            <Upload className="h-4 w-4" />
            Replace with file
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void handleStartRecording()}
            className="gap-2 text-slate-500"
          >
            <Mic className="h-4 w-4" />
            Re-record
          </Button>
        </div>
      )}
    </div>
  )
}
